import { useState } from "react";
import axios, { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

interface WandError {
  code: string;
  detail: string;
}

const DEFAULT_ERROR_MESSAGE =
  "An unexpected error occurred. Please try again later.";

export function useErrorHandling() {
  const [error, setError] = useState<string | null>(null);

  const handleError = (error: unknown, context?: Record<string, any>) => {
    // Send to Sentry for tracking
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext("additional_context", context);
      }
      
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<WandError>;
        
        // Add API-specific context
        scope.setTag("error_type", "api_error");
        scope.setContext("api_details", {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          url: axiosError.config?.url,
          method: axiosError.config?.method,
          data: axiosError.response?.data,
        });
        
        Sentry.captureException(error);
        setError(axiosError.response?.data?.detail || DEFAULT_ERROR_MESSAGE);
      } else {
        scope.setTag("error_type", "application_error");
        Sentry.captureException(error);
        setError(DEFAULT_ERROR_MESSAGE);
      }
    });
  };

  const clearError = () => {
    setError(null);
  };

  return [error, handleError, clearError, setError] as const;
}
